<script setup lang="ts">
import { DronecastCaster } from '@/sdk'
import { kurentoUtils } from '@/utils/kurentoUtils'
import { useLocalInput } from '@/composables/useLocalStorage'
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useDronecastStore } from '@/stores/dronecastStore'
import CasterStatusBar from '@/components/CasterStatusBar.vue'
import { monitoringService } from '@/services/monitoring.service'
import { useAccessControl } from '@/composables/useAccessControl'
import { useRecordingControl } from '@/composables/useRecordingControl'
import type { MediaServerType } from '@dronecast/shared'

const caster = ref<InstanceType<typeof DronecastCaster> | null>(null)
const route = useRoute()
const store = useDronecastStore()
const websocketUrl = computed(() => store.websocketUrl)

const sourceType = ref<'webcam' | 'screen'>('webcam')
// 미디어 서버는 Browserless(headless relay) 단일이다 — ADR-B25
const mediaServerType = ref<MediaServerType>('headless')
const gcsId = useLocalInput('dronecast.gcsId', route.query?.gcsId as string || 'KETI_GCS')
const droneName = useLocalInput('dronecast.droneName', route.query?.droneName as string || 'drone01')
const cameraName = useLocalInput('dronecast.cameraName', route.query?.cameraName as string || 'camera01')
const bitrate = ref('')

const isCasting = ref(false)
const yoloEnabled = ref(false)

const { isRecording, start: startRecording, stop: stopRecording } = useRecordingControl()

const onClickCast = () => {
  if (!gcsId.value || !droneName.value || !cameraName.value) {
    alert('GCS, Drone, Camera를 입력해주세요.')
    return
  }
  if (!caster.value) return
  caster.value.cast({ gcs: gcsId.value, drone: droneName.value, camera: cameraName.value })
  isCasting.value = true
}

const onClickStop = () => {
  caster.value?.stop()
  isCasting.value = false
  if (isRecording.value) stopRecording()
}

const onCastroomStatusChanged = (result: { castroomId: any; status: string }) => {
  if (result.status === 'casting') {
    monitoringService.updateCastingStatus(true, 0)
  } else if (result.status === 'closed' || result.status === 'stopped') {
    isCasting.value = false
    if (isRecording.value) stopRecording()
    monitoringService.updateCastingStatus(false, 0)
  }
}

const onWsStateChanged = (state: 'connecting' | 'connected' | 'reconnecting' | 'disconnected') => {
  monitoringService.updateSignalingState('caster', state)
}

const onWebRtcStateChanged = (_role: 'caster', connectionState?: string, iceState?: string) => {
  monitoringService.updateWebRtcState('caster', connectionState, iceState)
}

const onViewerListChanged = (viewerList: string[]) => {
  monitoringService.updateCastingStatus(isCasting.value, viewerList.length)
}

const { canRecord, notifyLoginRequired } = useAccessControl()
const onClickRecording = () => {
  if (!canRecord.value) {
    notifyLoginRequired('녹화')
    return
  }
  if (isRecording.value) {
    stopRecording()
  } else {
    startRecording({ gcs: gcsId.value, drone: droneName.value, camera: cameraName.value })
  }
}

const toggleYolo = () => { yoloEnabled.value = !yoloEnabled.value }

const openDroneCastPopup = () => {
  const params = new URLSearchParams({
    gcs: gcsId.value,
    drone: droneName.value,
    camera: cameraName.value,
    media: mediaServerType.value,
  })
  window.open(`/service/cast?${params}`, 'drone_popup', 'width=800,height=600')
}
</script>

<template>
  <main>
    <div class="title-row">
      <h1 class="page-title">스트림 송출 테스트</h1>
      <div class="source-selector">
        <label class="radio-label">
          <input type="radio" v-model="sourceType" value="webcam" :disabled="isCasting" /> WebCam
        </label>
        <label class="radio-label">
          <input type="radio" v-model="sourceType" value="screen" :disabled="isCasting" /> 화면공유
        </label>
      </div>
    </div>

    <div class="input-box">
      <div class="input-fields">
        <label>GCS</label>
        <input type="text" v-model="gcsId" class="stream-select" :disabled="isCasting" />
        <label>Drone</label>
        <input type="text" v-model="droneName" class="stream-select" :disabled="isCasting" />
        <label>Camera</label>
        <input type="text" v-model="cameraName" class="stream-select" :disabled="isCasting" />
        <label>비트레이트</label>
        <input type="text" v-model="bitrate" class="stream-select" style="max-width:80px" :disabled="isCasting" placeholder="kbps" />
      </div>

      <div class="action-row">
        <div class="cast-action-group">
          <button v-if="!isCasting" class="btn btn-primary" @click="onClickCast">송출 시작</button>
          <button v-else class="btn btn-danger" @click="onClickStop">송출 중지</button>
        </div>
        <button class="btn btn-secondary" @click="openDroneCastPopup">드론용 송출 페이지</button>
      </div>
    </div>

    <div class="toolbar">
      <CasterStatusBar />
      <div class="toolbar-buttons">
        <button
          class="btn btn-sm"
          :class="[isRecording ? 'btn-danger' : 'btn-secondary', { 'btn-locked': !canRecord }]"
          :disabled="canRecord && !isCasting"
          :title="!canRecord ? '녹화 기능은 로그인 후 사용 가능합니다' : ''"
          @click="onClickRecording"
        >{{ isRecording ? '녹화 중지' : '녹화 시작' }}</button>
        <button class="btn btn-sm" :class="yoloEnabled ? 'btn-success' : 'btn-secondary'" @click="toggleYolo" :disabled="!isCasting">YOLO {{ yoloEnabled ? 'ON' : 'OFF' }}</button>
      </div>
    </div>

    <div class="video-container">
      <DronecastCaster
        ref="caster"
        :castroomId="{ gcs: gcsId, drone: droneName, camera: cameraName }"
        :wsUrl="websocketUrl"
        :kurentoUtils="kurentoUtils"
        :source="sourceType"
        :showPreview="true"
        :mediaServerType="mediaServerType"
        :detectionEnabled="yoloEnabled"
        @onCastroomStatusChanged="onCastroomStatusChanged"
        @onWsStateChanged="onWsStateChanged"
        @onWebRtcStateChanged="onWebRtcStateChanged"
        @onViewerListChanged="onViewerListChanged"
      />
    </div>

    <div class="status-legend">
      <span class="legend-item"><span class="dot idle"></span> 대기</span>
      <span class="legend-item"><span class="dot casting"></span> 송출 중</span>
      <span class="legend-item"><span class="dot stopped"></span> 중지됨</span>
      <span class="legend-item"><span class="dot error"></span> 오류</span>
      <span class="legend-label">현재: {{ isCasting ? '송출 중' : '대기' }}</span>
    </div>
  </main>
</template>
